import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "../../context/ApiContext";
import { Card } from "../ui/Card";
import { Button } from "../ui/Button";
import { Spinner } from "../ui/Spinner";
import { Alert } from "../ui/Alert";
import { PageBuilder as PageBuilderComponent } from "../pageBuilder/PageBuilder";
import { PageBuilderProvider } from "../../context/PageBuilderContext";
import { IPageBuilder, IComponentNode } from "../../types/pageBuilder.types";
import { FaEdit, FaPlus, FaTimes, FaCheck } from "react-icons/fa";

interface PolicyPage {
  _id: string;
  slug: string;
  name?: string;
  isCreated: boolean;
  isPublished: boolean;
  layout?: IComponentNode | null;
  pageType?: string;
}

const POLICY_TYPES = [
  { slug: "privacy", titleKey: "admin.setupWizard.steps.policies.types.privacy", required: true },
  { slug: "terms", titleKey: "admin.setupWizard.steps.policies.types.terms", required: true },
  { slug: "returns", titleKey: "admin.setupWizard.steps.policies.types.returns", required: false },
  { slug: "shipping", titleKey: "admin.setupWizard.steps.policies.types.shipping", required: false },
];

interface PolicyPagesEditorProps {
  onSuccess?: () => void;
}

export const PolicyPagesEditor: React.FC<PolicyPagesEditorProps> = ({ onSuccess }) => {
  const { t } = useTranslation();
  
  const getPolicyTitle = useCallback((policyType: typeof POLICY_TYPES[number]) => {
    return t(policyType.titleKey);
  }, [t]);
  const { api } = useApi();
  const [pages, setPages] = useState<PolicyPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creatingPage, setCreatingPage] = useState(false);
  const [selectedPolicyType, setSelectedPolicyType] = useState<string | null>(null);
  const [editingPage, setEditingPage] = useState<IPageBuilder | null>(null);
  const [editedPages, setEditedPages] = useState<Record<string, boolean>>({});

  const checkEditedPages = useCallback(async () => {
    try {
      // Always check all policy types, regardless of whether they exist in pages state
      // The backend will check if the page exists and if it's edited
      const editedChecks = await Promise.all(
        POLICY_TYPES.map(async (policyType) => {
          try {
            const response = await api.get(`/store/policy-page/${policyType.slug}/edited`);
            return { slug: policyType.slug, isEdited: response.data.isEdited || false };
          } catch (err: any) {
            console.warn(`Failed to check if ${policyType.slug} is edited:`, err);
            return { slug: policyType.slug, isEdited: false };
          }
        })
      );

      const editedMap: Record<string, boolean> = {};
      editedChecks.forEach((check) => {
        editedMap[check.slug] = check.isEdited;
      });
      setEditedPages(editedMap);
    } catch (err: any) {
      console.warn("Failed to check edited pages:", err);
    }
  }, [api]);

  const loadPages = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch from unified PageModel - policy pages are now static type
      const response = await api.get("/pages?type=static&includeInactive=true");
      const allPages = response.data.pages || [];
      
      const policySlugs = POLICY_TYPES.map(pt => pt.slug);
      const pageMap = new Map<string, PolicyPage>();

      // Populate with default policy types
      POLICY_TYPES.forEach((policyType) => {
        pageMap.set(policyType.slug, {
          _id: "",
          name: getPolicyTitle(policyType),
          slug: policyType.slug,
          isCreated: false,
          isPublished: false,
          layout: null,
          pageType: "static", // Policy pages are now static type
        });
      });

      // Merge pages from unified model
      allPages.forEach((page: any) => {
        const normalizedSlug = (page.slug || "").toLowerCase().trim();
        
        // Check if this is a policy page by slug (case-insensitive)
        const isPolicyPage = policySlugs.some(slug => slug.toLowerCase() === normalizedSlug);
        
        if (isPolicyPage) {
          // Find the matching policy type to get the correct slug (exact match from POLICY_TYPES)
          const matchingPolicyType = POLICY_TYPES.find(pt => pt.slug.toLowerCase() === normalizedSlug);
          const correctSlug = matchingPolicyType ? matchingPolicyType.slug : normalizedSlug;
          
          // Ensure the page exists in our map (it should, but double-check)
          if (!pageMap.has(correctSlug)) {
            // If not in map, add it as a default entry first
            pageMap.set(correctSlug, {
              _id: "",
              name: matchingPolicyType ? getPolicyTitle(matchingPolicyType) : correctSlug,
              slug: correctSlug,
              isCreated: false,
              isPublished: false,
              layout: null,
              pageType: "static",
            });
          }
          
          // If layout is empty but zones exist, merge zones into layout for editing
          let layout = page.layout;
          if ((!layout || !layout.children || layout.children.length === 0) && page.zones && Object.keys(page.zones).length > 0) {
            const zoneChildren: IComponentNode[] = Object.values(page.zones);
            layout = {
              id: "root",
              type: "container",
              props: {},
              styles: { default: { padding: "24px" } },
              children: zoneChildren,
            };
          }

          // Update the page in the map with the actual page data
          const existingPage = pageMap.get(correctSlug);
          if (existingPage) {
            pageMap.set(correctSlug, {
              ...existingPage,
              _id: page._id,
              name: page.title || page.name || existingPage.name,
              isCreated: true,
              isPublished: page.isActive !== undefined ? page.isActive : page.isPublished,
              layout: layout || null,
              pageType: page.pageType || page.type || "static", // Policy pages are now static
            });
          }
        }
      });

      setPages(Array.from(pageMap.values()));
    } catch (err: any) {
      setError(err.response?.data?.error || t("admin.setupWizard.steps.policies.errors.load"));
      console.error("Error loading policy pages:", err);
    } finally {
      setLoading(false);
    }
  }, [api, t, getPolicyTitle]);

  useEffect(() => {
    loadPages();
  }, [loadPages]);

  useEffect(() => {
    // Check which pages are edited after pages are loaded
    // Also check on initial load even if pages array is empty (to check all policy types)
    checkEditedPages();
  }, [pages, checkEditedPages]);

  const handleEditPage = async (pageId: string) => {
    try {
      setError(null);
      
      // Get from unified PageModel - policy pages are now static type
      const response = await api.get(`/pages?type=static&includeInactive=true`);
      const pages = response.data.pages || [];
      const page = pages.find((p: any) => p._id === pageId);
      
      if (!page) {
        setError(t("admin.setupWizard.steps.policies.errors.pageNotFound"));
        return;
      }
      
      // If layout is empty but zones exist, merge zones into layout for editing
      let pageLayout = page.layout;
      if ((!pageLayout || !pageLayout.children || pageLayout.children.length === 0) && page.zones && Object.keys(page.zones).length > 0) {
        const zoneChildren: IComponentNode[] = Object.values(page.zones);
        pageLayout = {
          id: "root",
          type: "container",
          props: {},
          styles: { default: { padding: "24px" } },
          children: zoneChildren,
        };
      }
      
      // If still empty, create a default container
      if (!pageLayout || !pageLayout.children || pageLayout.children.length === 0) {
        pageLayout = {
          id: `layout-${page.slug}-${Date.now()}`,
          type: "container",
          props: {},
          styles: { default: { padding: "24px" } },
          children: [],
        };
      }
      
      const pageName = page.title || page.name || "";
      setEditingPage({
        _id: page._id,
        storeId: page.storeId || "",
        name: pageName,
        slug: page.slug,
        isCreated: true,
        isPublished: page.isActive !== undefined ? page.isActive : (page.isPublished !== undefined ? page.isPublished : false),
        layout: pageLayout,
        pageType: page.pageType || page.type || "policy",
        createdAt: page.createdAt || new Date(),
        updatedAt: page.updatedAt || new Date(),
      } as IPageBuilder);
    } catch (err: any) {
      setError(err.response?.data?.error || t("admin.setupWizard.steps.policies.errors.edit"));
      console.error("Error editing page:", err);
    }
  };

  const handleSavePage = async (layout: IComponentNode) => {
    if (!editingPage) return;

    try {
      setError(null);
      
      // Save to unified PageModel (no longer need to sync to separate PageBuilderModel)
      if (editingPage._id) {
        // Update existing page
        await api.put(`/pages/${editingPage._id}`, {
          layout,
        });
      } else {
        // Create new page
        await api.post("/pages", {
          title: editingPage.name,
          slug: editingPage.slug,
          type: "static", // Policy pages are now static type
          layout,
          isActive: editingPage.isPublished,
        });
      }
      
      await loadPages();
      setEditingPage(null);
      // Re-check edited status after saving
      // Check immediately first, then check again after a delay to ensure backend has fully processed
      await checkEditedPages();
      setTimeout(async () => {
        await checkEditedPages();
      }, 1500);
      // Call onSuccess after a short delay to ensure backend has processed the save
      // This ensures the setup progress is updated correctly
      setTimeout(() => {
        if (onSuccess) {
          onSuccess();
        }
      }, 500);
    } catch (err: any) {
      setError(err.response?.data?.error || t("admin.setupWizard.steps.policies.errors.save"));
    }
  };

  const handleCancelEdit = () => {
    setEditingPage(null);
  };

  const handleTogglePublish = async (pageId: string, currentStatus: boolean) => {
    try {
      setError(null);
      // Update isActive in /pages endpoint (PageModel) to keep it in sync with admin panel
      await api.put(`/pages/${pageId}`, {
        isActive: !currentStatus,
      });
      await loadPages();
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t("admin.setupWizard.steps.policies.errors.publish"));
    }
  };

  const handleCreatePage = async (policyType: string) => {
    try {
      setCreatingPage(true);
      setError(null);
      const policyTypeData = POLICY_TYPES.find((pt) => pt.slug === policyType);
      
      // Create default empty layout for policy page
      const defaultLayout = {
        id: `layout-${policyType}-${Date.now()}`,
        type: "container",
        props: {},
        styles: {
          default: {
            padding: "24px",
          },
        },
        children: [],
      };
      
      // Create in /pages endpoint (PageModel) to keep it in sync with admin panel
      await api.post("/pages", {
        title: policyTypeData ? getPolicyTitle(policyTypeData) : policyType,
        slug: policyType,
        type: "static", // Policy pages are now static type
        layout: defaultLayout,
        isActive: false, // Start as inactive (draft), user can publish later
      });

      await loadPages();
      setSelectedPolicyType(null);
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t("admin.setupWizard.steps.policies.errors.create"));
    } finally {
      setCreatingPage(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner size="lg" />
      </div>
    );
  }

  // Show page builder editor when editing
  if (editingPage) {
    // Ensure layout has proper structure - if it's empty or missing children, create a default
    let pageLayout = editingPage.layout;
    
    if (!pageLayout || !pageLayout.children || pageLayout.children.length === 0) {
      // If layout is empty, create a default container
      pageLayout = {
        id: pageLayout?.id || `layout-${editingPage.slug}-${Date.now()}`,
        type: "container",
        props: {},
        styles: {
          default: {
            padding: "24px",
          },
        },
        children: [],
      };
    }

    return (
      <PageBuilderProvider initialLayout={pageLayout}>
        <div className="fixed inset-0 flex flex-col bg-gray-100 z-[9999]">
          <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {t("admin.setupWizard.steps.policies.editingPage")}: {editingPage.name}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {t("admin.setupWizard.steps.policies.pageType")}: {editingPage.pageType || "static"}
              </p>
            </div>
            <Button
              onClick={handleCancelEdit}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <FaTimes />
              {t("common.cancel")}
            </Button>
          </div>
          <div className="flex-1 overflow-hidden">
            <PageBuilderComponent
              initialLayout={pageLayout}
              onCancel={handleCancelEdit}
              onSave={handleSavePage}
              pageName={editingPage.slug}
              pageType={editingPage.pageType || "static"}
            />
          </div>
        </div>
      </PageBuilderProvider>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm text-gray-600 mb-4">
          {t("admin.setupWizard.steps.policies.description")}
        </p>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      <div className="space-y-3">
        {POLICY_TYPES.map((policyType) => {
          const existingPage = pages.find((p) => p.slug === policyType.slug);
          const isCreated = !!existingPage && !!existingPage._id && existingPage._id.length > 0;
          const isEdited = editedPages[policyType.slug] || false;

          return (
            <Card
              key={policyType.slug}
              padding="sm"
              className={isEdited ? "border-green-200 bg-green-50" : ""}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{getPolicyTitle(policyType)}</span>
                    {policyType.required && (
                      <span className="text-xs text-red-600">*</span>
                    )}
                    {isEdited && (
                      <span className="text-xs text-green-600">✓ {t("common.configured")}</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {isCreated
                      ? existingPage.isPublished
                        ? t("common.published")
                        : t("common.draft")
                      : t("admin.setupWizard.steps.policies.notCreated")}
                  </div>
                </div>
                <div className="flex gap-2">
                  {isCreated ? (
                    <>
                      {!existingPage.isPublished && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleTogglePublish(existingPage._id, existingPage.isPublished)}
                          className="text-green-600 border-green-300 hover:bg-green-50"
                        >
                          <FaCheck className="mr-1" />
                          {t("admin.setupWizard.steps.policies.publish")}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditPage(existingPage._id)}
                      >
                        <FaEdit className="mr-1" />
                        {t("common.edit")}
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => handleCreatePage(policyType.slug)}
                      loading={creatingPage && selectedPolicyType === policyType.slug}
                    >
                      <FaPlus className="mr-1" />
                      {t("common.create")}
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

